import { useContext, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setDistrictIds,
  setLocalLevelIds,
  setProvinceIds,
} from "../../../slices/adminvalues";
import "ol/ol.css";
import VectorSource from "ol/source/Vector";
import ClusterSource from "ol/source/Cluster";
import AnimatedClusterLayer from "ol-ext/layer/AnimatedCluster";
import VectorLayer from "ol/layer/Vector";
import SelectCluster from "ol-ext/interaction/SelectCluster";
import GeoJSON from "ol/format/GeoJSON";
import { Fill, Style, Icon, Circle, Text, Stroke } from "ol/style";
import MapContext from "../MapContext";
import dots from "../../../assets/icons/DOTS.png";
import { boundingExtent } from "ol/extent";
import { TU_BASE_URL } from "../../../constants";
import { transformExtent } from "ol/proj";
import bbox from "@turf/bbox";
import { stringToColour } from "../../../utils/generateColor";

const styleCache = {};
var padding = [50, 50, 30, 150];

function getIconStyle(properties) {
  const icon = new Icon({
    src: dots,
    anchor: [10, 10],
    anchorXUnits: "pixels",
    anchorYUnits: "pixels",
    scale: [0.9, 0.9],
  });
  return new Style({
    image: icon,
  });
}
const vectorSource = new VectorSource({});
const clusterSource = new ClusterSource({
  distance: 35,
  source: vectorSource,
});
const clusterLayer = new AnimatedClusterLayer({
  id: "cluster-layer",
  zIndex: 9999,
  source: clusterSource,
  style: (feature, resolution) => {
    var size = feature.get("features")?.length;
    if (size && size > 1) {
      const color = "#ff9202dd";
      const radius = Math.max(8, Math.min(size * 0.75, 20));
      const style = (styleCache[size] = new Style({
        image: new Circle({
          radius: radius,
          fill: new Fill({
            color,
          }),
        }),
        text: new Text({
          text: size.toString(),
          fill: new Fill({
            color: "#fff",
          }),
        }),
      }));
      return style;
    }
    const properties = feature.get("features")[0].getProperties();
    return getIconStyle(properties);
  },
});
const provinceSource = new VectorSource({});
const provinceLayer = new VectorLayer({
  id: "province-layer",
  source: provinceSource,
  zIndex: 111,
  style: (feature, resolution) =>
    new Style({
      text: new Text({
        text: feature.getProperties()["name_en"],
        fillColor: "#fff",
        font: '20px "Lucida Grande",Verdana,Geneva,Lucida,Arial,Helvetica,sans-serif',
      }),
      stroke: new Stroke({
        color: "#000000",
        width: 1.5,
      }),
      fill: new Fill({
        color: stringToColour(feature.getProperties()["name_en"]),
      }),
    }),
});
const districtSource = new VectorSource({});
const districtLayer = new VectorLayer({
  id: "district-layer",
  source: districtSource,
  zIndex: 222,
  style: (feature, resolution) =>
    new Style({
      text: new Text({
        text: feature.getProperties()["name_en"],
        fillColor: "#fff",
        font: '16px "Lucida Grande",Verdana,Geneva,Lucida,Arial,Helvetica,sans-serif',
      }),
      stroke: new Stroke({
        color: "#000000",
        width: 1,
      }),
      fill: new Fill({
        color: stringToColour(feature.getProperties()["name_en"]),
      }),
    }),
});
const localLevelSource = new VectorSource({});
const localLevelLayer = new VectorLayer({
  id: "local-level-layer",
  source: localLevelSource,
  zIndex: 222,
  style: (feature, resolution) =>
    new Style({
      text: new Text({
        text: feature.getProperties()["name_en"],
        fillColor: "#fff",
        font: '10px "Lucida Grande",Verdana,Geneva,Lucida,Arial,Helvetica,sans-serif',
      }),
      stroke: new Stroke({
        color: "#000000",
        width: 0.5,
      }),
      fill: new Fill({
        color: stringToColour(feature.getProperties()["name_en"]),
      }),
    }),
});

const selectCluster = new SelectCluster({
  layers: (layer) => {
    return layer === clusterLayer;
  },
  pointRadius: 40,
  maxObjects: 8,
  animate: true,
  autoClose: false,
  style: (feature, resolution) => {
    const features = feature.get("features");
    if (features) {
      var size = feature.get("features").length;
      if (size && size > 1) {
        const color = "#ff9202dd";
        const radius = Math.max(8, Math.min(size * 0.75, 20));
        const style = (styleCache[size] = new Style({
          image: new Circle({
            radius: radius,
            fill: new Fill({
              color,
            }),
          }),
          text: new Text({
            text: size.toString(),
            fill: new Fill({
              color: "#fff",
            }),
          }),
        }));
        return style;
      }
      const properties = features[0].getProperties();
      return getIconStyle(properties);
    }
  },
  featureStyle: (feature, resolution) => {
    const properties = feature.get("features")?.[0]?.getProperties();
    const style = new Style({});
    if (properties) {
      style.setImage(getIconStyle(properties).getImage());
    }

    return style;
  },
});
export default function SDPLayer() {
  const dispatch = useDispatch();
  const features = useSelector((state) => state.admin.hfs_filtered);
  const layerVisibility = useSelector((state) => state.layer);
  const filter = useSelector((state) => state.admin.adminFilter);
  const { map } = useContext(MapContext);

  useEffect(() => {
    if (!map) return;
    map.addLayer(clusterLayer);
    map.addLayer(provinceLayer);
    map.addLayer(districtLayer);
    map.addLayer(localLevelLayer);
    map.addInteraction(selectCluster);
    return () => {
      map.removeLayer(clusterLayer);
      map.removeLayer(provinceLayer);
      map.removeLayer(districtLayer);
      map.removeLayer(localLevelLayer);
      map.removeInteraction(selectCluster);
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;

    map.on("singleclick", (e) => {
      const { pixel } = e;
      clusterLayer.getFeatures(pixel).then((clickedFeatures) => {
        console.log(clickedFeatures);
        const allPointFeatures = map
          .getFeaturesAtPixel(pixel)
          .filter((e) => e.get("features")?.length);
        console.log(allPointFeatures, 1);
        if (clickedFeatures.length) {
          const features = clickedFeatures[0].get("features");
          if (features.length === 1) {
            const properties = features[0].getProperties();
            console.log(properties);
          } else if (features.length > 1) {
            const extent = boundingExtent(
              features.map((r) => r.getGeometry().getCoordinates())
            );
            if (extent[0] === extent[2] && extent[1] === extent[3]) {
              return;
            } else {
              map.getView().fit(extent, {
                size: map.getSize(),
                padding,
              });
            }
          }
        } else if (allPointFeatures.length) {
          console.log(allPointFeatures, 2);
        } else {
          localLevelLayer.getFeatures(pixel).then((clickedFeatures) => {
            if (clickedFeatures.length) {
              const local_level_id = clickedFeatures[0].getProperties()["id"];
              dispatch(setLocalLevelIds([local_level_id]));
            } else {
              districtLayer.getFeatures(pixel).then((clickedFeatures) => {
                if (clickedFeatures.length) {
                  const district_id = clickedFeatures[0].getProperties()["id"];
                  dispatch(setDistrictIds([district_id]));
                } else {
                  provinceLayer.getFeatures(pixel).then((clickedFeatures) => {
                    if (clickedFeatures.length) {
                      const province_id =
                        clickedFeatures[0].getProperties()["id"];
                      dispatch(setProvinceIds([province_id]));
                    }
                  });
                }
              });
            }
          });
        }
      });
    });

    return () => {};
  }, [dispatch, map]);

  useEffect(() => {
    provinceLayer.setVisible(layerVisibility.provincesVisible);
  }, [layerVisibility.provincesVisible]);
  useEffect(() => {
    districtLayer.setVisible(layerVisibility.districtsVisible);
  }, [layerVisibility.districtsVisible]);
  useEffect(() => {
    localLevelLayer.setVisible(layerVisibility.localLevelVisible);
  }, [layerVisibility.localLevelVisible]);
  useEffect(() => {
    if (!map) return;

    if (filter.local_level_ids.length)
      fetch(
        `${TU_BASE_URL}locallevelgeojson/?locallevel_ids=${filter.local_level_ids}`
      )
        .then(function (response) {
          return response.json();
        })
        .then(function (json) {
          localLevelSource.clear();
          localLevelSource.addFeatures(
            new GeoJSON({
              featureProjection: "EPSG:3857",
            }).readFeatures(json)
          );
          const extent = transformExtent(bbox(json), "EPSG:4326", "EPSG:3857");
          map.getView().fit(extent, map.getSize());
        });
    else if (filter.district_ids.length)
      fetch(
        `${TU_BASE_URL}locallevelgeojson/?district_ids=${filter.district_ids}`
      )
        .then(function (response) {
          return response.json();
        })
        .then(function (json) {
          localLevelSource.clear();
          localLevelSource.addFeatures(
            new GeoJSON({
              featureProjection: "EPSG:3857",
            }).readFeatures(json)
          );
          const extent = transformExtent(bbox(json), "EPSG:4326", "EPSG:3857");
          map.getView().fit(extent, map.getSize());
        });
    else if (filter.province_ids.length)
      fetch(
        `${TU_BASE_URL}districtgeojson/?province_ids=${filter.province_ids}`
      )
        .then(function (response) {
          return response.json();
        })
        .then(function (json) {
          districtSource.clear();
          localLevelSource.clear();
          districtSource.addFeatures(
            new GeoJSON({
              featureProjection: "EPSG:3857",
            }).readFeatures(json)
          );
          const extent = transformExtent(bbox(json), "EPSG:4326", "EPSG:3857");
          map.getView().fit(extent, map.getSize());
        });
    else
      fetch(`${TU_BASE_URL}provincegeojson/`)
        .then(function (response) {
          return response.json();
        })
        .then(function (json) {
          localLevelSource.clear();
          districtSource.clear();
          provinceSource.clear();
          provinceSource.addFeatures(
            new GeoJSON({
              featureProjection: "EPSG:3857",
            }).readFeatures(json)
          );
          const extent = transformExtent(bbox(json), "EPSG:4326", "EPSG:3857");
          map.getView().fit(extent, map.getSize());
        });
    return () => {};
  }, [map, filter.local_level_ids, filter.district_ids, filter.province_ids]);

  useEffect(() => {
    let selectedFeatures = [];
    if (layerVisibility.dotsVisible)
      selectedFeatures = [...selectedFeatures, ...features.dots];
    vectorSource.clear();
    vectorSource.addFeatures(
      new GeoJSON({
        featureProjection: "EPSG:3857",
      }).readFeatures({
        type: "FeatureCollection",
        features: selectedFeatures,
      })
    );
    return () => {};
  }, [features.dots, layerVisibility.dotsVisible]);
  return null;
}
